import { useState } from "react";
import { Resolver, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import { OutlinedButton, PrimaryButton } from "@normasteaching/norma-ui";
import { Modal } from "../../../UI/Modal/Modal.tsx";
import {
  BillingFormFields,
  BillingFormSection,
} from "../../../NormasTeachingCheckout/BillingFormSection.tsx";
import {
  bonificoBillingSchemaFields,
  hasSdiOrPec,
} from "../../../BonificoCheckout/bonificoBillingValidation.ts";
import {
  StripeOrderDTO,
  StripeOrderStatus,
} from "../../../Stripe/StripeOrderDTO.ts";
import { useInstallmentsSettlementSummary } from "./useInstallmentsSettlementSummary.ts";
import { useSettleInstallmentsByBankTransfer } from "./useSettleInstallmentsByBankTransfer.ts";
import {
  getInstallmentsSettlementErrorMessage,
  InstallmentsSettlementSummaryDTO,
  PaidAmountSource,
} from "./InstallmentsSettlementDTO.ts";
import {
  toRequestedInvoiceInfo,
  toSettlementBillingDefaults,
  toSettlementBillingDefaultsOfDraft,
} from "./settlementBilling.ts";
import { Article7TerSwitch } from "../../BonificoOrder/Article7TerSwitch.tsx";
import {
  BonificoOrderDTO,
  VatExemption,
} from "../../../ProductService/BonificoOrder/BonificoOrderDTO.ts";
import { isArticle7Ter } from "../../../ProductService/BonificoOrder/article7Ter.ts";
import { useCustomerBonificoDrafts } from "../../../ProductService/BonificoOrder/useCustomerBonificoDrafts.ts";
import { CustomerDraftChoice } from "./CustomerDraftChoice.tsx";

type FormValues = BillingFormFields & {
  amountInEuros: number;
  receiptDate: string;
  isStripeCancellationAcknowledged: boolean;
};

function formatEuros(amountInCents: number): string {
  return `€${(amountInCents / 100).toFixed(2)}`;
}

function createSchema(remainingAmountInCents: number) {
  return yup
    .object({
      amountInEuros: yup
        .number()
        .typeError("Inserisci un importo valido")
        .moreThan(0, "L'importo deve essere maggiore di zero")
        .max(
          remainingAmountInCents / 100,
          `L'importo non può superare quanto resta da pagare (${formatEuros(remainingAmountInCents)})`,
        )
        .required("Importo obbligatorio"),
      receiptDate: yup
        .string()
        .required("La data della contabile è obbligatoria"),
      isStripeCancellationAcknowledged: yup
        .boolean()
        .oneOf([true], "Conferma di aver verificato la contabile"),
      ...bonificoBillingSchemaFields,
    })
    .test("has-at-least-sdi-or-pec", "", hasSdiOrPec);
}

function settlementBillingDefaultsOf(
  order: StripeOrderDTO,
  chosenDraft?: BonificoOrderDTO,
): BillingFormFields {
  return chosenDraft
    ? toSettlementBillingDefaultsOfDraft(chosenDraft)
    : toSettlementBillingDefaults(order.invoiceInfo);
}

function bonificoOrderOutcomeOf(chosenDraft?: BonificoOrderDTO): string {
  if (!chosenDraft)
    return "Verrà creato un ordine bonifico in attesa di pagamento: fattura (numerazione Bonifici Italia) e riga BigQuery partiranno quando lo segnerai come pagato alla data valuta. Corsi e deal restano invariati.";
  if (chosenDraft.valueDateUtc)
    return "La bozza bonifico scelta diventerà il saldo. Ha già la data valuta, quindi passerà subito in «Fattura da emettere»: fattura (numerazione Bonifici Italia) e riga BigQuery partiranno quando emetterai la fattura. Corsi e deal restano invariati.";
  return "La bozza bonifico scelta diventerà il saldo, in attesa di pagamento: fattura (numerazione Bonifici Italia) e riga BigQuery partiranno quando la segnerai come pagata alla data valuta. Corsi e deal restano invariati.";
}

function SummaryRow({
  label,
  amountInCents,
  isHighlighted = false,
}: {
  label: string;
  amountInCents: number;
  isHighlighted?: boolean;
}) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-gray-600">{label}</span>
      <span
        className={
          isHighlighted
            ? "font-bold text-gray-900"
            : "font-medium text-gray-900"
        }
      >
        {formatEuros(amountInCents)}
      </span>
    </div>
  );
}

function SettlementForm({
  order,
  summary,
  customerDrafts,
  onClose,
  onSettled,
}: {
  order: StripeOrderDTO;
  summary: InstallmentsSettlementSummaryDTO;
  customerDrafts: BonificoOrderDTO[];
  onClose: () => void;
  onSettled: (order: StripeOrderDTO) => void;
}) {
  const { settleInstallments, loading } = useSettleInstallmentsByBankTransfer();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [chosenDraft, setChosenDraft] = useState<BonificoOrderDTO | undefined>(
    customerDrafts[0],
  );
  const [isArticle7TerApplied, setIsArticle7TerApplied] = useState(
    isArticle7Ter(customerDrafts[0]?.vatExemption),
  );
  const remainingAmountInCents = summary.remainingAmountInCents.amount;
  const {
    control,
    register,
    handleSubmit,
    watch,
    trigger,
    reset,
    getValues,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(
      createSchema(remainingAmountInCents),
    ) as unknown as Resolver<FormValues>,
    defaultValues: {
      ...settlementBillingDefaultsOf(order, customerDrafts[0]),
      amountInEuros: remainingAmountInCents / 100,
      receiptDate: "",
      isStripeCancellationAcknowledged: false,
    },
  });

  const chooseDraft = (draft?: BonificoOrderDTO) => {
    setChosenDraft(draft);
    setIsArticle7TerApplied(isArticle7Ter(draft?.vatExemption));
    reset({ ...getValues(), ...settlementBillingDefaultsOf(order, draft) });
  };

  const onSubmit = async (values: FormValues) => {
    setSubmitError(null);
    try {
      const settled = await settleInstallments(order._id, {
        amountInCents: Math.round(values.amountInEuros * 100),
        receiptDate: values.receiptDate,
        invoiceInfo: toRequestedInvoiceInfo(values),
        ...(isArticle7TerApplied && { vatExemption: VatExemption.Article7Ter }),
        ...(chosenDraft && { bonificoDraftOrderId: chosenDraft.orderId }),
      });
      onSettled(settled);
    } catch (e) {
      setSubmitError(
        getInstallmentsSettlementErrorMessage(
          e,
          "Errore durante la registrazione del saldo. Riprova.",
        ),
      );
    }
  };

  return (
    <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
      {customerDrafts.length > 0 && (
        <CustomerDraftChoice
          drafts={customerDrafts}
          chosenDraft={chosenDraft}
          onChoose={chooseDraft}
        />
      )}

      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium text-gray-700">
          Importo del saldo (€) *
        </label>
        <input
          type="number"
          step="0.01"
          min="0"
          {...register("amountInEuros")}
          className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 max-w-xs"
        />
        <p className="text-xs text-gray-500">
          Precompilato con quanto resta da pagare: modificalo se il cliente ha
          versato un importo diverso.
        </p>
        {errors.amountInEuros && (
          <p className="text-xs text-red-500">{errors.amountInEuros.message}</p>
        )}
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium text-gray-700">
          Data della contabile *
        </label>
        <input
          type="date"
          max={dayjs().format("YYYY-MM-DD")}
          {...register("receiptDate")}
          className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 max-w-xs"
        />
        {errors.receiptDate && (
          <p className="text-xs text-red-500">{errors.receiptDate.message}</p>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <p className="text-sm font-medium text-gray-700">
          Dati di fatturazione del saldo
        </p>
        <BillingFormSection
          control={control}
          register={register}
          errors={errors}
          invoiceRequired={true}
          businessType={watch("businessType")}
          taxCode={watch("taxCode")}
          isBusinessInformationRequired={true}
          isInvoiceMandatory={true}
          trigger={trigger}
          askBillingCountry
          onArticle7TerSuggestion={setIsArticle7TerApplied}
        />
        <Article7TerSwitch
          checked={isArticle7TerApplied}
          onChange={setIsArticle7TerApplied}
        />
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-900 flex flex-col gap-1">
        {summary.paidAmountSource === PaidAmountSource.Stripe ? (
          <p>
            La sottoscrizione Stripe verrà cancellata e le rate ancora aperte
            annullate: l'operazione non è reversibile.
          </p>
        ) : (
          <p>
            L'ordine verrà segnato come saldato: l'operazione non è reversibile.
          </p>
        )}
        <p>{bonificoOrderOutcomeOf(chosenDraft)}</p>
        {order.status === StripeOrderStatus.InstallmentDelinquent && (
          <p className="font-medium">
            L'ordine è insoluto: l'accesso alla piattaforma verrà riattivato
            subito.
          </p>
        )}
      </div>

      <label className="flex items-start gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          {...register("isStripeCancellationAcknowledged")}
          className="mt-0.5 w-4 h-4 accent-primary"
        />
        Ho verificato la contabile del bonifico
      </label>
      {errors.isStripeCancellationAcknowledged && (
        <p className="text-xs text-red-500 -mt-3">
          {errors.isStripeCancellationAcknowledged.message}
        </p>
      )}

      {submitError && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
          {submitError}
        </p>
      )}

      <div className="flex justify-between gap-2">
        <OutlinedButton label="Annulla" onClick={onClose} disabled={loading} />
        <PrimaryButton
          label="Registra saldo"
          onClick={handleSubmit(onSubmit)}
          loading={loading}
          disabled={loading}
        />
      </div>
    </form>
  );
}

export function InstallmentsSettlementModal({
  order,
  onClose,
  onSettled,
}: {
  order: StripeOrderDTO;
  onClose: () => void;
  onSettled: (order: StripeOrderDTO) => void;
}) {
  const { summary, error, isLoading } = useInstallmentsSettlementSummary(
    order._id,
  );
  const {
    drafts: customerDrafts,
    error: customerDraftsError,
    isLoading: areCustomerDraftsLoading,
  } = useCustomerBonificoDrafts(order.customerInformation.email);
  const isSettleable = !!summary && !summary.blockingReason;

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Saldo rate con bonifico"
      size="lg"
    >
      <div className="flex flex-col gap-4 text-left max-h-[75vh] overflow-y-auto pr-1">
        <p className="text-sm text-gray-600">
          {order.customerInformation.firstName}{" "}
          {order.customerInformation.lastName} ·{" "}
          {order.customerInformation.email}
        </p>

        {isLoading && (
          <p className="text-sm text-gray-500">Calcolo della rimanenza...</p>
        )}

        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
            {getInstallmentsSettlementErrorMessage(
              error,
              "Impossibile recuperare le rate pagate su Stripe. Riprova.",
            )}
          </p>
        )}

        {summary?.paidAmountSource === PaidAmountSource.OrderHistory && (
          <p className="text-sm text-blue-900 bg-blue-50 border border-blue-200 rounded-lg p-3">
            Stripe non ha più i dati di questa sottoscrizione: il già pagato è
            ricostruito dallo storico dell'ordine ({order.numberOfPaidCharges}{" "}
            rate pagate su {order.numberOfInstallments}). Verificalo con la
            contabile prima di registrare il saldo.
          </p>
        )}

        {summary && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex flex-col gap-1.5">
            <SummaryRow
              label="Totale ordine (al netto del coupon)"
              amountInCents={summary.totalAmountInCents.amount}
            />
            <SummaryRow
              label={
                summary.paidAmountSource === PaidAmountSource.Stripe
                  ? "Già pagato su Stripe"
                  : "Già pagato (dallo storico dell'ordine)"
              }
              amountInCents={summary.amountAlreadyPaidInCents.amount}
            />
            <SummaryRow
              label="Resta da pagare"
              amountInCents={summary.remainingAmountInCents.amount}
              isHighlighted
            />
          </div>
        )}

        {summary?.blockingReason && (
          <>
            <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-3">
              {summary.blockingReason}
            </p>
            <div>
              <OutlinedButton label="Chiudi" onClick={onClose} />
            </div>
          </>
        )}

        {isSettleable && areCustomerDraftsLoading && (
          <p className="text-sm text-gray-500">
            Ricerca delle bozze bonifico del cliente...
          </p>
        )}

        {isSettleable && customerDraftsError && (
          <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-3">
            Non è stato possibile cercare le bozze bonifico del cliente:
            registrando il saldo verrà creato un nuovo ordine bonifico.
          </p>
        )}

        {isSettleable && !areCustomerDraftsLoading && (
          <SettlementForm
            order={order}
            summary={summary}
            customerDrafts={customerDrafts}
            onClose={onClose}
            onSettled={onSettled}
          />
        )}
      </div>
    </Modal>
  );
}
